import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScrollLock } from "../../../hooks/useScrollLock";

import {
  FiPackage,
  FiDollarSign,
  FiActivity,
  FiUser,
  FiCalendar,
  FiCheck,
  FiClock,
  FiXCircle,
  FiEye,
  FiX,
  FiFileText,
  FiChevronRight,
  FiChevronLeft,
  FiChevronDown,
  FiEdit3,
  FiLoader,
} from "react-icons/fi";
import {
  Btn,
  FormSelect,
  OrderStatus,
  PageSection,
  Table,
  TableActions,
  TableContainer,
  TableHeader,
  TableTitle,
  OrderDetailCard,
  OrderDetailLabel,
  OrderDetailValue,
  OrderItemsSection,
  OrderItemCard,
  OrderItemImage,
  OrderItemInfo,
  OrderItemName,
  OrderItemPrice,
  OrderItemQuantity,
  OrderItemSize,
  CustomerInfoSection,
  CustomerInfoGrid,
  OrderSummarySection,
  OrderSummaryGrid,
  CloseBtn,
  TableTh,
  TableRow,
  TableCell,
  PaginationButton,
  PaginationInfo,
  PaginationContainer,
  LoadingContainer,
  Relative,
  StatusBadge,
  StatusChangeModal,
  StatusChangeContent,
  StatusOptionsContainer,
  StatusOption,
  StatusChangeTitle,
} from "./BandOrdersPage.styles";

import { formatPrice } from "../../../utils/utils";

import { selectUser } from "../../../store/slices/auth/slice";
import { getBandOrders } from "../../../store/slices/bandDashboard/asyncThunks";

import {
  selectBandOrders,
  clearAllErrors,
} from "../../../store/slices/bandDashboard/slice";

import { toast } from "../../../components/modern-ui/sonner";
import {
  Modal,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "../AdminProductsPage/AdminProductsPage.styles";
import BandLayout from "../BandLayout/BandLayout";
import { updateOrderStatus } from "../../../store/slices/orders/asyncThunks";
import {
  clearOrderById,
  selectOrders,
} from "../../../store/slices/orders/slice";

const BandOrdersPage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const bandOrders = useSelector(selectBandOrders);
  const orders = useSelector(selectOrders);

  const [orderModal, setOrderModal] = useState({
    show: false,
    order: null,
  });
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [activeModal, setActiveModal] = useState(null);
  const [statusChangeOrderId, setStatusChangeOrderId] = useState(null);

  useScrollLock(orderModal.show);

  const showOrderDetails = (order) => {
    setOrderModal({ show: true, order });
  };

  const closeOrderModal = () => {
    setOrderModal({ show: false, order: null });
  };

  const loadOrdersPage = async (page, statusFilter = orderStatusFilter) => {
    const bandId = user.bandId;
    if (!bandId) return;

    try {
      const params = {
        page,
        limit: 10,
      };

      // Only add status filter if it's not "all"
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      await dispatch(
        getBandOrders({
          bandId: user.bandId,
          params,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error loading orders page:", error);
      toast.error("Greška pri učitavanju stranice");
    }
  };

  // Handle filter changes - reset to page 1 when filter changes
  const handleStatusFilterChange = (newFilter) => {
    setOrderStatusFilter(newFilter);
    setCurrentPage(1);
  };

  // Load orders when currentPage changes OR when orderStatusFilter changes
  useEffect(() => {
    if (user && user.role === "band" && user?.bandId) {
      loadOrdersPage(currentPage, orderStatusFilter);
    }
  }, [user?.bandId, currentPage, orderStatusFilter]);

  // Clear errors on component unmount
  useEffect(() => {
    return () => {
      dispatch(clearAllErrors());
    };
  }, [dispatch]);

  const getFilteredOrders = () => {
    // Since we're now filtering at API level, just return the data
    return bandOrders.data || [];
  };

  const getOrderStatusText = (status) => {
    const statusTexts = {
      pending: "Na čekanju",
      processing: "U obradi",
      delivered: "Završeno",
      shipped: "Poslano",
      canceled: "Otkazano",
    };
    return statusTexts[status] || status;
  };

  const statusOptions = [
    { value: "pending", label: "Na čekanju" },
    { value: "processing", label: "U obradi" },
    { value: "shipped", label: "Poslano" },
    { value: "delivered", label: "Dostavljeno" },
    { value: "canceled", label: "Otkazano" },
  ];

  const getOrderStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FiClock />;
      case "processing":
        return <FiActivity />;
      case "delivered":
        return <FiCheck />;
      case "canceled":
        return <FiXCircle />;
      default:
        return <FiClock />;
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setStatusChangeOrderId(null);
    dispatch(clearOrderById());
  };

  const goToPage = async (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = async () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = async () => {
    const totalPages = Math.ceil((bandOrders.total || 0) / 10);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Funkcija za menjanje statusa
  const updateStatus = async (orderId, newStatus) => {
    try {
      await dispatch(updateOrderStatus({ id: orderId, status: newStatus }))
        .unwrap()
        .then(() => {
          dispatch(
            getBandOrders({
              bandId: user.bandId,
              params: { page: currentPage, limit: 10 },
            })
          );
          closeModal();
        });
    } catch (error) {
      console.error("Failed to update order status:", error);
    }
  };
  const openModal = (modalId) => {
    setActiveModal(modalId);
  };
  const openStatusChangeModal = (orderId) => {
    setStatusChangeOrderId(orderId);
    openModal("changeStatus");
  };

  return (
    <BandLayout>
      <PageSection>
        <TableHeader>
          <TableTitle>Sve Porudžbine</TableTitle>
          <TableActions>
            <Relative>
              <FormSelect
                style={{ width: "fit-content", paddingRight: 35 }}
                value={orderStatusFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
              >
                <option value="all">Sve porudžbine</option>
                <option value="pending">Na čekanju</option>
                <option value="processing">U obradi</option>
                <option value="delivered">Završene</option>
                <option value="canceled">Otkazane</option>
              </FormSelect>
              <FiChevronDown
                color="#ff6b35"
                size={20}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </Relative>
          </TableActions>
        </TableHeader>

        <TableContainer>
          {bandOrders.loading ? (
            <LoadingContainer>
              <l-line-spinner
                size="32"
                stroke="2.5"
                speed="1"
                color="#ff6b35"
              />
              Učitavanje porudžbina...
            </LoadingContainer>
          ) : (
            <>
              <Table>
                <thead>
                  <tr>
                    <TableTh>ID</TableTh>
                    <TableTh>Proizvod</TableTh>
                    <TableTh>Kupac</TableTh>
                    <TableTh>Količina</TableTh>
                    <TableTh>Cena</TableTh>
                    <TableTh>Datum</TableTh>
                    <TableTh>Status</TableTh>
                    <TableTh>Akcije</TableTh>
                  </tr>
                </thead>
                <tbody>
                  {bandOrders.loading ? (
                    <TableRow>
                      <TableCell
                        colSpan="8"
                        style={{ textAlign: "center", padding: "2rem" }}
                      >
                        Učitavanje porudžbina...
                      </TableCell>
                    </TableRow>
                  ) : getFilteredOrders()?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          padding: "2rem",
                          color: "#cccccc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "1.2rem",
                            marginBottom: "0.5rem",
                          }}
                        >
                          <FiFileText size={32} color="#666" />
                        </div>
                        <div>
                          {orderStatusFilter === "all"
                            ? "Nemate porudžbine"
                            : `Nema porudžbina sa statusom "${getOrderStatusText(
                                orderStatusFilter
                              )}"`}
                        </div>
                        <div
                          style={{
                            fontSize: "0.9rem",
                            marginTop: "0.5rem",
                            opacity: 0.7,
                          }}
                        >
                          {orderStatusFilter === "all"
                            ? "Porudžbine će se pojaviti kada kupci naruče vaše proizvode"
                            : "Promenite filter da vidite ostale porudžbine"}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    getFilteredOrders().map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{order.id}</TableCell>
                        <TableCell>
                          {order.orderItems?.[0]?.product?.name || "N/A"}
                        </TableCell>
                        <TableCell>
                          {order.firstName +
                            " " +
                            order.lastName.charAt(0) +
                            "."}
                        </TableCell>
                        <TableCell>
                          {order.orderItems?.reduce(
                            (sum, item) => sum + item.quantity,
                            0
                          ) || 0}
                        </TableCell>
                        <TableCell>
                          {formatPrice(order.bandItemsPrice)}
                        </TableCell>
                        <TableCell>
                          {new Date(order.createdAt).toLocaleDateString(
                            "sr-Latn-RS"
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            $status={order.status}
                            onClick={() => openStatusChangeModal(order.id)}
                          >
                            {getOrderStatusIcon(order.status)}

                            {getOrderStatusText(order.status)}
                            <FiEdit3 />
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <Btn
                            className="secondary sm"
                            onClick={() => showOrderDetails(order)}
                          >
                            <FiEye />
                            Detalji
                          </Btn>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </tbody>
              </Table>
              <PaginationContainer>
                <PaginationInfo>
                  Prikazuje se {(currentPage - 1) * 10 + 1}-
                  {Math.min(currentPage * 10, bandOrders.total || 0)} od{" "}
                  {bandOrders.total || 0} porudžbina
                </PaginationInfo>

                <div>
                  <PaginationButton
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1 || bandOrders?.loading}
                  >
                    <FiChevronLeft />
                  </PaginationButton>

                  {Array.from(
                    { length: Math.ceil((bandOrders.total || 0) / 10) },
                    (_, i) => i + 1
                  ).map((page) => (
                    <PaginationButton
                      key={page}
                      onClick={() => goToPage(page)}
                      $active={currentPage === page}
                      disabled={bandOrders?.loading}
                    >
                      {page}
                    </PaginationButton>
                  ))}

                  <PaginationButton
                    onClick={goToNextPage}
                    disabled={
                      currentPage === Math.ceil((bandOrders.total || 0) / 10) ||
                      bandOrders?.loading
                    }
                  >
                    <FiChevronRight />
                  </PaginationButton>
                </div>
              </PaginationContainer>
            </>
          )}
        </TableContainer>
      </PageSection>

      {/* Order Details Modal */}
      <Modal $active={orderModal.show}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Detalji Porudžbine #{orderModal.order?.id}</ModalTitle>
            <CloseBtn onClick={closeOrderModal}>
              <FiX />
            </CloseBtn>
          </ModalHeader>

          {orderModal.order && (
            <>
              {/* Customer Information */}
              <CustomerInfoSection>
                <h4>Informacije o Kupcu</h4>
                <CustomerInfoGrid>
                  <OrderDetailCard>
                    <OrderDetailLabel>
                      <FiUser /> Kupac
                    </OrderDetailLabel>
                    <OrderDetailValue>
                      {orderModal.order.firstName}{" "}
                      {orderModal.order.lastName?.charAt(0)}.
                    </OrderDetailValue>
                  </OrderDetailCard>
                </CustomerInfoGrid>
              </CustomerInfoSection>

              {/* Order Items */}
              <OrderItemsSection>
                <h4>Stavke Porudžbine</h4>
                {orderModal.order.orderItems?.map((item) => (
                  <OrderItemCard key={item.id}>
                    <OrderItemImage>
                      <FiPackage size={40} />
                    </OrderItemImage>
                    <OrderItemInfo>
                      <OrderItemName>{item.name}</OrderItemName>
                      <OrderItemSize>Veličina: {item.size}</OrderItemSize>
                      <OrderItemQuantity>
                        Količina: {item.quantity}
                      </OrderItemQuantity>
                    </OrderItemInfo>
                    <OrderItemPrice>
                      <div>Jedinična cena: {formatPrice(item.itemPrice)}</div>
                      <div>
                        <strong>Ukupno: {formatPrice(item.totalPrice)}</strong>
                      </div>
                    </OrderItemPrice>
                  </OrderItemCard>
                ))}
              </OrderItemsSection>

              {/* Order Summary */}
              <OrderSummarySection>
                <h4>Rezime Porudžbine</h4>
                <OrderSummaryGrid>
                  <OrderDetailCard>
                    <OrderDetailLabel>
                      <FiCalendar /> Datum Narudžbe
                    </OrderDetailLabel>
                    <OrderDetailValue>
                      {new Date(orderModal.order.createdAt).toLocaleDateString(
                        "sr-Latn-RS",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )}
                    </OrderDetailValue>
                  </OrderDetailCard>
                  <OrderDetailCard>
                    <OrderDetailLabel>Status</OrderDetailLabel>
                    <OrderDetailValue>
                      <OrderStatus $status={orderModal.order.status}>
                        {getOrderStatusIcon(orderModal.order.status)}
                        {getOrderStatusText(orderModal.order.status)}
                      </OrderStatus>
                    </OrderDetailValue>
                  </OrderDetailCard>
                  <OrderDetailCard>
                    <OrderDetailLabel>
                      <FiDollarSign /> Ukupna Cena
                    </OrderDetailLabel>
                    <OrderDetailValue>
                      <strong>
                        {formatPrice(orderModal.order.totalPrice)}
                      </strong>
                    </OrderDetailValue>
                  </OrderDetailCard>
                  <OrderDetailCard>
                    <OrderDetailLabel>Vaš Deo</OrderDetailLabel>
                    <OrderDetailValue>
                      <strong>
                        {formatPrice(orderModal.order.bandItemsPrice || 0)}
                      </strong>
                    </OrderDetailValue>
                  </OrderDetailCard>
                </OrderSummaryGrid>
              </OrderSummarySection>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Modal za promenu statusa */}
      <StatusChangeModal $active={activeModal === "changeStatus"}>
        <StatusChangeContent>
          <ModalHeader>
            <StatusChangeTitle>Promeni Status Porudžbine</StatusChangeTitle>
            <ModalClose onClick={closeModal}>
              <FiX />
            </ModalClose>
          </ModalHeader>

          <StatusOptionsContainer>
            {statusOptions.map((option) => (
              <StatusOption
                key={option.value}
                $status={option.value}
                onClick={() => updateStatus(statusChangeOrderId, option.value)}
                disabled={orders.updateLoading}
              >
                {getOrderStatusIcon(option.value)}
                {orders.updateLoading ? <FiLoader size={16} /> : option.label}
              </StatusOption>
            ))}
          </StatusOptionsContainer>
        </StatusChangeContent>
      </StatusChangeModal>
    </BandLayout>
  );
};

export default BandOrdersPage;
